import { Link } from 'react-router-dom';
import ScrollReveal from '@/components/common/ScrollReveal';
import GradientIcon from '@/components/common/GradientIcon';
import BrandLogo from '@/components/common/BrandLogo';
import { trustedPlatforms } from '@/mocks/landing';

/** Floating 3D-style icon tile around the product window. */
function FloatTile({ icon, tone, className }: { icon: string; tone: 'blue' | 'violet' | 'pink' | 'orange'; className: string }) {
  return (
    <div
      className={`absolute hidden sm:flex w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/90 backdrop-blur border border-white shadow-[0_12px_30px_-8px_rgba(47,81,214,0.35)] items-center justify-center ${className}`}
    >
      <GradientIcon icon={icon} tone={tone} size="text-3xl md:text-4xl" />
    </div>
  );
}

export default function Hero() {
  return (
    <section className="relative w-full overflow-hidden bg-gradient-to-b from-[#e9e6ff] via-[#f3f1ff] to-white pt-28 md:pt-32">
      {/* soft colour blobs */}
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-[70%] w-[520px] h-[520px] rounded-full bg-[#c9d6ff] blur-[110px] opacity-70" />
      <div className="pointer-events-none absolute top-10 left-1/2 translate-x-[10%] w-[460px] h-[460px] rounded-full bg-[#f5d5ff] blur-[110px] opacity-60" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-6 text-center">
        <ScrollReveal animation="fade-up">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight bg-gradient-to-r from-[#2f51d6] via-[#6b5cf0] to-[#d9348a] bg-clip-text text-transparent">
            LoyaltyBridge
          </h1>
          <p className="mt-3 text-2xl sm:text-3xl md:text-4xl font-bold text-[#1f2329] tracking-tight">
            Make every purchase a reason to{' '}
            <span className="bg-gradient-to-r from-[#2f51d6] to-[#d9348a] bg-clip-text text-transparent">come back</span>
          </p>
          <p className="mt-4 text-sm md:text-base text-[#646a73] max-w-xl mx-auto">
            The plug-in reward &amp; redeem engine. Points, tiers, rewards and sponsors through one secure API.
          </p>
        </ScrollReveal>

        <ScrollReveal animation="fade-up" delay={120}>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/signup"
              className="group inline-flex items-center gap-3 pl-6 pr-1.5 h-12 rounded-full bg-gradient-to-r from-[#3b5bfd] via-[#6b5cf0] to-[#ff7a9a] text-white font-semibold shadow-[0_10px_30px_-10px_rgba(91,92,240,0.8)] hover:brightness-110 transition-all"
            >
              Start free
              <span className="w-9 h-9 rounded-full bg-white/25 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                <i className="ri-arrow-right-line text-lg" />
              </span>
            </Link>
          </div>
        </ScrollReveal>

        {/* Product window */}
        <ScrollReveal animation="fade-up" delay={220} className="relative mt-12 md:mt-16 mx-auto max-w-3xl [perspective:1600px]">
          <FloatTile icon="ri-coins-fill" tone="orange" className="-left-6 md:-left-14 top-24 -rotate-12" />
          <FloatTile icon="ri-gift-2-fill" tone="pink" className="right-24 -top-8 rotate-6" />
          <FloatTile icon="ri-vip-crown-2-fill" tone="blue" className="-right-6 md:-right-12 top-32 rotate-12" />

          <div className="rounded-[28px] bg-white/70 backdrop-blur-xl border border-white shadow-[0_30px_80px_-30px_rgba(47,81,214,0.45)] p-3 md:p-4 [transform:rotateX(14deg)] origin-bottom">
            <div className="rounded-[20px] bg-gradient-to-b from-white to-[#f7f8ff] border border-[#eef0ff] p-5 md:p-8 text-left">
              <div className="flex items-center justify-center gap-2">
                <BrandLogo className="w-7 h-7" />
                <span className="font-heading font-bold text-lg text-[#1f2329]">Rewards assistant</span>
              </div>
              <p className="text-center text-xs text-[#8f959e] mt-1">Award, check and redeem in one place</p>

              <div className="mt-5 rounded-2xl border border-[#e3e7ff] bg-white p-4 shadow-sm">
                <p className="text-sm text-[#8f959e]">Award 150 points to mem_8f3k2 for order #10482…</p>
                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#eef2ff] text-[#2f51d6] px-2.5 py-1">
                    <i className="ri-coins-line" /> Earn
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#fdeef5] text-[#d9348a] px-2.5 py-1">
                    <i className="ri-gift-line" /> Redeem
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#ecfaf5] text-[#0e9f7e] px-2.5 py-1">
                    <i className="ri-wallet-3-line" /> Balance
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 text-[#646a73]">
                    POST /earn
                    <span className="w-7 h-7 rounded-full bg-gradient-to-r from-[#2f51d6] to-[#6b5cf0] text-white flex items-center justify-center">
                      <i className="ri-send-plane-fill" />
                    </span>
                  </span>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-3">
                {[
                  { k: 'Points issued', v: '2.4M', c: 'text-[#2f51d6]' },
                  { k: 'Redemptions', v: '18,240', c: 'text-[#d9348a]' },
                  { k: 'Active members', v: '96.1K', c: 'text-[#0e9f7e]' },
                ].map((s) => (
                  <div key={s.k} className="rounded-xl bg-white border border-[#eff0f1] px-3 py-2.5">
                    <div className="text-[11px] text-[#8f959e]">{s.k}</div>
                    <div className={`font-heading font-bold text-base md:text-lg ${s.c}`}>{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>

      {/* Trusted by */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-12">
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {trustedPlatforms.map((name) => (
            <span key={name} className="font-heading text-base md:text-lg font-bold text-[#a3a8b0] tracking-tight">
              {name}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
